import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import useProcessMessageFlowMutation from 'hooks/useProcessMessageFlowMutation';
import { PropertyOwner } from 'models/Owner';
import { PROPERTY_OWNERS_BASE_QUERY_KEY } from '../../usePropertyOwners';
import SendOwnerInviteModal from './SendOwnerInviteModal';
import { SendOwnerInviteSchemaFormValues } from './SendOwnerInviteModal.schema';

const sendOwnerInviteModalId = 'send-invite-modal';

const useSendOwnerInviteModal = (owner: PropertyOwner) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { uid: propertyUid } = useParams();
  const { openModal, closeModal } = useAppModal();
  const { notifySuccess, notifyError } = useNotify();
  const { mutateAsync: sendInvite } = useProcessMessageFlowMutation({
    onError: () => {
      notifyError(t('pageProperty.owners.inviteModal.sendInviteError'));
    },
    onSuccess: (data) => {
      if (data.status === 'error') {
        notifyError(t('pageProperty.owners.inviteModal.sendInviteError'));
        return;
      }

      notifySuccess(t('pageProperty.owners.inviteModal.sendInviteSuccess'));
      closeModal(sendOwnerInviteModalId);
      queryClient.invalidateQueries({
        queryKey: [PROPERTY_OWNERS_BASE_QUERY_KEY, 'detail', propertyUid],
      });
    },
  });

  const handleSubmit = (formValues: SendOwnerInviteSchemaFormValues) => {
    return sendInvite({
      bcc: formValues.sendCopyToAgency ?? true,
      case: '16',
      content: formValues.content_htmlContent ?? '',
      ownerUid: owner.uid,
      showEditTemplateButton: false,
      subject: formValues.subject,
      to: formValues.to,
    });
  };

  const openSendOwnerInviteModal = () => {
    openModal({
      id: sendOwnerInviteModalId,
      customContent: (
        <SendOwnerInviteModal
          onClose={() => closeModal(sendOwnerInviteModalId)}
          onSubmit={handleSubmit}
          owner={owner}
        />
      ),
    });
  };

  return {
    openSendOwnerInviteModal,
  };
};

export default useSendOwnerInviteModal;
